import * as React from 'react';

import { useIntl } from '../../hooks';
import { cn } from '../../lib/utils';
import { Button } from './button.js';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20] as const;

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  page: number;
  totalPages: number;
  pageSize: number;
  pageSizeOptions?: readonly number[];
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  previousLabel?: string;
  nextLabel?: string;
  perPageLabel?: (count: number) => string;
  rangeLabel?: (range: { start: number; end: number; total: number }) => string;
}

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      className,
      page,
      totalPages,
      pageSize,
      pageSizeOptions,
      total,
      onPageChange,
      onPageSizeChange,
      previousLabel,
      nextLabel,
      perPageLabel,
      rangeLabel,
      ...props
    },
    ref,
  ) => {
    const intl = useIntl();

    const effectivePrev = previousLabel ?? intl.formatMessage({ id: 'common.previous' });
    const effectiveNext = nextLabel ?? intl.formatMessage({ id: 'common.next' });
    const effectivePerPage =
      perPageLabel ??
      ((count: number) => intl.formatMessage({ id: 'devicesList.perPage' }, { count }));
    const effectiveRange =
      rangeLabel ??
      (({ start, end, total: t }: { start: number; end: number; total: number }) =>
        intl.formatMessage({ id: 'devicesList.pagination' }, { start, end, total: t }));
    const effectiveOptions = pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

    const end = Math.min(page * pageSize, total);
    const start = end === 0 ? 0 : (page - 1) * pageSize + 1;

    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-between gap-2 text-xs text-foreground', className)}
        {...props}
      >
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="rounded-md border border-border bg-background-light px-3 py-2 text-sm shadow-sm"
          data-test-id="devices-per-page"
        >
          {effectiveOptions.map((size) => (
            <option key={size} value={size}>
              {effectivePerPage(size)}
            </option>
          ))}
        </select>
        {totalPages > 1 && (
          <div className="flex items-center gap-4">
            <span>{effectiveRange({ start, end, total })}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(Math.max(1, page - 1))}
              >
                {effectivePrev}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              >
                {effectiveNext}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
Pagination.displayName = 'Pagination';

export { Pagination };
